import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from "@angular/core";
import {
  AbstractControl,
  NonNullableFormBuilder,
  ValidationErrors,
  Validators
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { AdvParkService } from "@onsip/common/services/api/resources/parkingLot/parking-lot.service";
import { views } from "../../../app/phone/views";
import { Observable, of, Subscription } from "rxjs";
import { MusicSource } from "@onsip/common/services/api/resources/musicSource/music-source";
import { map, tap } from "rxjs/operators";
import { MusicSourceService } from "@onsip/common/services/api/resources/musicSource/music-source.service";
import { EditMusicSourceDialogComponent } from "@onsip/web/features/shared/components/editMusicSourceDialog/edit-music-source-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import { SipAddressFieldComponent } from "../../shared/components/sipAddressField/sip-address-field.component";
import { ModalMaterialComponent } from "../../shared/components/modal/modal-material.component";
import { NAME_VALIDATOR_REGEX_SPACE_ONLY } from "@onsip/common/libraries/validators/name-validators";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";

const manualCallReturnBtns: Array<string> = [
  "",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "#",
  "*"
];

const numberOfChannels: Array<string> = ["15", "30", "45", "60", "75", "90"];
const autoReturnTimeout: Array<string> = [
  "30",
  "60",
  "120",
  "180",
  "240",
  "300",
  "360",
  "420",
  "480",
  "540",
  "600"
];

@Component({
  template: "",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ParkingLotFormComponent implements OnDestroy {
  constructor(
    protected translate: TranslateService,
    protected parkLotService: AdvParkService,
    protected musicSourceService: MusicSourceService,
    protected dialog: MatDialog,
    protected fb: NonNullableFormBuilder,
    protected userService: UserService
  ) {}
  parkSuffix = ".park";
  retrieveSuffix = ".retrieve";
  views = views;
  featurePricing!: string;
  currentPrice!: number;
  allExtensions: Array<string> = [];
  allRetrieverExtensions: Array<string> = [];
  autoReturnTimeout = autoReturnTimeout;
  numberOfChannels = numberOfChannels;
  defaultChannelLimit = "15";
  manualReturnBtns = manualCallReturnBtns;

  @ViewChild("parkSipAddress") parkSipAddress!: SipAddressFieldComponent;
  @ViewChild("retrieverSipAddressField") retrieverSipAddressField!: SipAddressFieldComponent;
  @ViewChild("extensionsList") extensionsListRef!: ExtensionsListComponent;
  @ViewChild("retrieverExtensionsList") retrieverExtensionsListRef!: ExtensionsListComponent;

  parkLotForm = this.fb.group({
    name: [
      "",
      [
        Validators.required,
        Validators.maxLength(60),
        Validators.pattern(NAME_VALIDATOR_REGEX_SPACE_ONLY)
      ]
    ],
    channelLimit: ["", [Validators.required]],
    pin: [""],
    parkSipAddress: ["", [Validators.required]],
    extensions: [<Array<string>>[]],
    retrieveSipAddress: ["", [Validators.required]],
    retrieverExtensions: [<Array<string>>[]],
    musicSourceName: [""],
    musicSourceId: ["", [Validators.required]],
    manualReturnDtmf: [""],
    manualReturnEnable: [false],
    autoReturnTimeout: [this.autoReturnTimeout[0], [Validators.required]],
    returnAddress: ["", [Validators.required]],
    pinRetrievalEnable: [false]
  });

  private dialogOptions = {
    panelClass: ["mat-typography", "onsip-dialog-universal-style"],
    restoreFocus: false,
    autoFocus: false
  };
  protected channelsConfirmed = false;
  protected unsubscriber = new Subscription();

  cancelDialogOptions = {
    panelClass: ["mat-typography", "onsip-dialog-universal-style"],
    data: {
      title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
      message: this.translate.instant("ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"),
      primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
      secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
      primaryBtnFlat: true
    }
  };

  musicSources$: Observable<Array<MusicSource>> = this.musicSourceService.orgState.pipe(
    map(musicSources =>
      musicSources.sort((a, b) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }

        if (a.name.toLowerCase() < b.name.toLowerCase()) {
          return -1;
        }

        return 0;
      })
    )
  );

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  // sip address param should be the original address. The sipAddress component holds the new and updated version of the sip address
  updateSipAddress(
    sipAddress: string,
    domain: string,
    sipAddressComponent: "parkSipAddress" | "retrieverSipAddressField"
  ): void {
    const aor = sipAddress + "@" + domain;
    this[sipAddressComponent].updateAppAddress(aor);
  }

  // Error Message Handling
  getErrorMessage(errors: ValidationErrors | null): string {
    if (errors?.invalidExtension || errors?.minLength) {
      return this.translate.instant(
        "ONSIP_I18N.EXTENSIONS_MUST_BE_BETWEEN_26_DIGITS_AND_BEGIN_WITH_EITHER__OR_A_NONZERO_DIGIT"
      );
    } else if (errors?.repeatExtension) {
      return this.translate.instant("ONSIP_I18N.EXTENSION_ALREADY_IN_USE");
    } else return "";
  }

  getGenericNameError(control: AbstractControl, value: string): string {
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : control.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.VALUE_IS_LONGER_THAN_LENGTH_CHARACTERS", {
          value,
          length: control.errors?.maxlength.requiredLength
        })
      : control.hasError("pattern")
      ? this.translate.instant("ONSIP_I18N.MUST_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_AND_SPACES")
      : "";
  }
  getGenericRequiredError(control: AbstractControl | undefined | null, value: string): string {
    // eslint-disable-next-line no-null/no-null
    if (control === undefined || control === null || control.touched) return "";
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : "";
  }

  calculateCurrentPrice(channelsCount: string): void {
    this.currentPrice =
      Math.round((Number(channelsCount) / 15) * Number(this.featurePricing) * 100) / 100;
  }

  createMusicSource(): void {
    this.dialog.open(EditMusicSourceDialogComponent, { ...this.dialogOptions });
  }

  protected initChannelLimitSubscription(): void {
    this.unsubscriber.add(
      this.parkLotForm.controls.channelLimit.valueChanges.subscribe(
        () => (this.channelsConfirmed = false)
      )
    );
  }

  protected showChannelsConfirmation(): Observable<boolean> {
    if (this.channelsConfirmed) {
      return of(true);
    }
    const amount = this.parkLotForm.value.channelLimit;
    const dialog = this.dialog.open(ModalMaterialComponent, {
      ...this.dialogOptions,
      data: {
        title: this.translate.instant("ONSIP_I18N.ARE_YOU_SURE_YOU_WANT_TO_ADD_AMOUNT_CHANNELS", {
          amount
        }),
        message: this.translate.instant(
          "ONSIP_I18N.YOU_ARE_ADDING_AMOUNT_CHANNELS_TO_YOUR_PARKING_LOT_YOUR_ACCOUNT_WILL_BE_CHARGED_PRICEMONTH__TAXES__FEES",
          { amount, price: this.currentPrice.toFixed(2) }
        ),
        primaryBtnFlat: true,
        primaryBtnText: this.translate.instant("ONSIP_I18N.ADD_CHANNELS"),
        secondaryBtnText: this.translate.instant("ONSIP_I18N.CANCEL")
      }
    });

    return dialog.afterClosed().pipe(
      map(res => res && res.doPrimaryAction),
      tap(res => (this.channelsConfirmed = res))
    );
  }
}
